import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';

import { MeasurementScale } from './MeasurementScale.js'

// WhiteGreenDot component matching Chakra UI version
const WhiteGreenDot = ({ size = "20px", dotColor = "#00ff00" }) => {
  return (
    <div
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        backgroundColor: 'white',
        borderRadius: '50%'
      }}
    >
      <div
        style={{
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '66%',
          height: '66%',
          backgroundColor: dotColor,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    </div>
  );
};

// ModelText component matching Chakra UI version
const ModelText = ({ text, variant = "right", dotColor = "#00ff00", dotSize = "12px" }) => {
  const isLeft = variant === 'left';

  const formatText = (input) => {
    if (typeof input !== 'string') return input;
    return input.split(/(\d+)/).map((part, i) =>
      /\d+/.test(part) ? <sub key={i} style={{ fontSize: '0.6em', verticalAlign: 'sub' }}>{part}</sub> : part
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: isLeft ? 'row-reverse' : 'row',
      alignItems: 'center',
      width: 'max-content',
      transform: isLeft ? 'translateX(-100%)' : 'none'
    }}>
      <div
        style={{
          borderRadius: '50%',
          width: dotSize,
          height: dotSize,
          backgroundColor: dotColor,
          flexShrink: 0
        }}
      />
      <div
        className="model-text"
        style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#425a70',
          whiteSpace: 'nowrap',
          paddingLeft: isLeft ? '0' : '10px',
          paddingRight: isLeft ? '10px' : '0'
        }}
      >
        {formatText(text)}
      </div>
    </div>
  );
};

export const Cubic = ({
  core,
  coreModification,
  coreColor,
  surfaceFunctionalization,
  surfaceFunctionalizationType = 'No modification',
  surfaceFunctionalizationColor,
  lineColor = "#00ff00",
  baseRotation = [0, 0, 0],
  isNanoformImageGenerated = false,
  measureScale = 0
}) => {
  const modelGroup = useRef();
  const y = baseRotation[1];

  useFrame(() => {
    if (modelGroup.current) {
      if (isNanoformImageGenerated) {
        modelGroup.current.rotation.y = y;
      } else {
        modelGroup.current.rotation.y += 0.005;
      }
    }
  });

  // Create box geometry
  const coreGeometry = new THREE.BoxGeometry(1.9, 1.9, 1.9);
  const surfaceGeometry = new THREE.BoxGeometry(2.15, 2.15, 2.15);

  return (
    <group>
      {/* Core label */}
      {Boolean(core) && (
        <group position={[0, 0, 0]}>
          <Html className="core" position={[1.2, 1, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={coreModification ? `${core?.name}/${coreModification}` : (core?.name || 'Core')}
              variant="right"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[0, 0, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [0, 0, 2],
              [0.7, 1, 2],
              [1.2, 1, 2],
            ]}
            lineWidth={3}
            color={lineColor}
          />
        </group>
      )}

      {/* Surface functionalization label */}
      {Boolean(surfaceFunctionalization) && (
        <group position={[0, 0, 0]}>
          <Html className="core--left" position={[-1.2, 0.945, 2]} zIndexRange={[0, 1]}>
            <ModelText 
              dotSize="12px" 
              text={surfaceFunctionalizationType} 
              variant="left" 
              dotColor={lineColor} 
            />
          </Html>
          <Html className="center" position={[-0.3, 0.65, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [-0.3, 0.65, 2],
              [-0.7, 0.9, 2],
              [-1.2, 0.9, 2],
            ]}
            lineWidth={1.5}
            color={lineColor}
          />
        </group>
      )}

      <MeasurementScale width={measureScale} unit="nm" color="#425a70" />

      {/* 3D Model */}
      <group ref={modelGroup} position={[0, 0, 0]} rotation={baseRotation}>
        {/* Core cube */}
        <mesh geometry={coreGeometry} castShadow>
          <meshStandardMaterial
            attach="material"
            roughness={0}
            metalness={0.1}
            color={coreColor}
          />
        </mesh>

        {/* Surface cube */}
        {Boolean(surfaceFunctionalization) && (
          <mesh geometry={surfaceGeometry} castShadow>
            <meshStandardMaterial 
              attach="material" 
              transparent 
              opacity={0.35} 
              color={surfaceFunctionalizationColor} 
            />
          </mesh>
        )}
      </group>
    </group>
  );
};